// Buffered "in-progress dish" for the logged-out publish flow.
//
// The backend requires a JWT to create/patch a listing, so a logged-out user's
// dish can't be saved server-side as they build it. Instead the publish form
// holds everything in React state (the same as for a logged-in user, minus the
// save-as-you-go PATCHes) and mirrors a snapshot here to localStorage so the
// dish survives a reload / app restart / magic-link return. Once the user
// creates an account at the final "Publish" step, the buffer is flushed to the
// backend (create + patch + image upload) and cleared.
//
// NOT persisted: a user-uploaded photo File. Files don't serialize to
// localStorage without heavy base64, so an uploaded photo is held in memory
// only — it survives the in-place signup (no reload) but is lost on a cold
// reload, after which the rest of the dish restores and the user re-adds it.
// A placeholder/library image is just a URL and persists fine.

import type { TimeWindowState } from './listingTimeWindow';
import type { LocationPickerValue } from '../components/location/LocationPicker';
import type { PatchListingRequest } from '../services/listingService';

const PENDING_LISTING_KEY = 'hp_pending_listing';

export interface PendingListingSnapshot {
  // UI form state — the source of truth the form renders from, so a restore
  // can repaint the form exactly as the user left it.
  title: string;
  description: string;
  spiciness: number;
  dietaryTags: string[];
  originTags: string[];
  allergenTags: string[];
  price: number;
  quantity: number;
  fulfillment: string[];
  shareLevel: number;
  /** The PICKUP window. Kept under its original key so buffers written before
   *  drop-off still restore (this blob is unversioned). */
  timeWindow: TimeWindowState;
  /** Null when the chef hasn't offered drop-off. Absent on older buffers. */
  dropoffWindow?: TimeWindowState | null;
  /** Miles, and the fee in whole DOLLARS (the form's unit — the wire format
   *  uses cents). Absent on buffers written before drop-off. */
  dropoffRadius?: number;
  dropoffFeeDollars?: number;
  location: LocationPickerValue | null;
  // Only ever a real URL (library/placeholder image). An in-memory uploaded
  // File's blob-preview URL is intentionally never written here.
  imageUrl: string | null;
  thumbnailUrl: string | null;
  // The wire-format patch buffer (accumulatedFields) — flushed verbatim onto
  // the listing once it's created post-signup. Persisted alongside the UI
  // state (captured together, so guaranteed consistent) to avoid re-deriving
  // wire values from form state on restore.
  accumulated: PatchListingRequest;
}

export function savePendingListing(snapshot: PendingListingSnapshot): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(PENDING_LISTING_KEY, JSON.stringify(snapshot));
  } catch {
    // Quota exceeded / disabled — degrade silently. The in-memory form still
    // holds the dish for this session; only a cold reload would lose it.
  }
}

export function loadPendingListing(): PendingListingSnapshot | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const stored = localStorage.getItem(PENDING_LISTING_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as PendingListingSnapshot;
  } catch {
    return null;
  }
}

export function clearPendingListing(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(PENDING_LISTING_KEY);
  } catch {
    // ignore
  }
}
